import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCities } from '../hooks/useCities';
import { useNavigate } from 'react-router-dom';

interface HeroSearchProps {
  searchRef?: React.RefObject<HTMLInputElement>;
}

const CATEGORIES = [
  'Música', 'Esportes', 'Teatro', 'Gastronomia', 'Arte', 'Tecnologia', 'Dança', 'Comédia',
];

const HeroSearch: React.FC<HeroSearchProps> = ({ searchRef }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const { t } = useTranslation();
  const { cities } = useCities();
  const navigate = useNavigate();

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCity) params.set('city', selectedCity);
    if (selectedDate) params.set('date', selectedDate);
    if (activeCategory) params.set('category', activeCategory);
    navigate(`/events?${params.toString()}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <section className="relative min-h-[520px]">
      <img
        src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1400&q=80&fit=crop"
        alt=""
        aria-hidden="true"
        loading="eager"
        onError={(e) => {
          e.currentTarget.src =
            'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1400&q=80&fit=crop';
        }}
        className="absolute inset-0 w-full h-full object-cover object-center bg-neutral-900"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/75" />
      <div className="relative px-4 md:px-margin-desktop py-20 md:py-28 max-w-container mx-auto">
        <h1 className="font-headline text-headline-lg-mobile md:text-headline-lg text-white mb-8 drop-shadow-lg text-center">
          Encontre e Reserve Eventos Incríveis
        </h1>

        {/* Search card */}
        <div className="bg-surface-container-lowest p-4 rounded-xl shadow-lg space-y-3 border border-outline-variant/30 max-w-3xl mx-auto mb-8">
          {/* Search input */}
          <div className="relative">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-primary">search</span>
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Buscar eventos..."
              className="w-full pl-10 pr-4 py-3 bg-surface-container-low border-none rounded-lg focus:ring-2 focus:ring-primary-container font-headline text-label-md text-on-surface-variant transition-all"
            />
          </div>

          {/* City + Date row */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-secondary">location_on</span>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="w-full pl-10 pr-2 py-3 bg-surface-container-low border-none rounded-lg focus:ring-2 focus:ring-primary-container font-headline text-label-md text-on-surface-variant transition-all appearance-none"
              >
                <option value="">Cidade</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.name}>{city.name}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-secondary">calendar_today</span>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full pl-10 pr-2 py-3 bg-surface-container-low border-none rounded-lg focus:ring-2 focus:ring-primary-container font-headline text-label-md text-on-surface-variant transition-all"
              />
            </div>
          </div>

          {/* Search button */}
          <button
            onClick={handleSearch}
            className="btn-primary w-full py-4 shadow-md flex items-center justify-center gap-2"
          >
            <span className="material-symbols-outlined text-[18px]">search</span>
            Buscar Eventos
          </button>
        </div>

        {/* Category chips */}
        <div className="flex overflow-x-auto gap-3 pb-2 no-scrollbar justify-center">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
              className={`chip px-6 py-2 active:scale-95 ${activeCategory === cat ? 'chip-active' : 'chip-idle'}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSearch;
